import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
  MessageSquare, MessageCircle, X, RefreshCcw, Send, Search, User, Eye, Bot, Archive, ArchiveRestore
} from "lucide-react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import MessageFormatter from "../../components/MessageFormatter";
import { logger } from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette for customer avatars - visually appealing gradients
const AVATAR_COLORS = [
  { from: 'from-blue-400', to: 'to-blue-600', text: 'text-white' },
  { from: 'from-green-400', to: 'to-green-600', text: 'text-white' },
  { from: 'from-purple-400', to: 'to-purple-600', text: 'text-white' },
  { from: 'from-pink-400', to: 'to-pink-600', text: 'text-white' },
  { from: 'from-orange-400', to: 'to-orange-600', text: 'text-white' },
  { from: 'from-teal-400', to: 'to-teal-600', text: 'text-white' },
  { from: 'from-indigo-400', to: 'to-indigo-600', text: 'text-white' },
  { from: 'from-rose-400', to: 'to-rose-600', text: 'text-white' },
  { from: 'from-cyan-400', to: 'to-cyan-600', text: 'text-white' },
  { from: 'from-amber-400', to: 'to-amber-600', text: 'text-white' },
];

// Get consistent color for a session based on session_id
const getAvatarColor = (sessionId) => {
  if (!sessionId) return AVATAR_COLORS[0];
  // Create a simple hash from the session ID
  let hash = 0;
  for (let i = 0; i < sessionId.length; i++) {
    hash = ((hash << 5) - hash) + sessionId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

// Helper component for session avatar
const SessionAvatar = ({ session }) => {
  const avatarColor = getAvatarColor(session.session_id);
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor.from} ${avatarColor.to} flex items-center justify-center ${avatarColor.text} font-semibold`}>
      {session.user_data?.name?.[0]?.toUpperCase() || "C"}
    </div>
  );
};

// Helper component for session status badge
const SessionStatusBadge = ({ session }) => {
  if (session.archived) {
    return (
      <Badge className="text-xs bg-gray-100 text-gray-600">
        <Archive className="w-3 h-3 mr-1" />
        Archived
      </Badge>
    );
  }

  if (session.status === "closed") {
    return (
      <Badge className="text-xs bg-gray-100 text-gray-600">
        <X className="w-3 h-3 mr-1" />
        Closed
      </Badge>
    );
  }

  if (session.status === "active") {
    return (
      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
        AI Active
      </Badge>
    );
  }

  if (session.status === "agent_active") {
    return (
      <Badge className="text-xs bg-green-100 text-green-800">
        Agent Active
      </Badge>
    );
  }

  if (session.status === "waiting_for_agent") {
    return (
      <Badge className="text-xs bg-yellow-100 text-yellow-800">
        Waiting
      </Badge>
    );
  }

  return null;
};

// Helper component for session action buttons in header
const SessionActionButtons = ({
  currentSession,
  selectedSession,
  isSupervising,
  setIsSupervising,
  handleReassignToAI,
  handleUnarchiveSession,
  handleArchiveSession,
  openReassignModal,
  handleCloseSession
}) => {
  const isAgentHandling = currentSession?.status === 'agent_active' || currentSession?.status === 'waiting_for_agent';
  const isClosed = currentSession?.status === 'closed';
  const isArchived = currentSession?.archived;

  return (
    <>
      {/* Archive/Unarchive/Reassign/Supervise buttons */}
      {isArchived && (
        <Button
          size="sm"
          variant="outline"
          className="text-blue-600 border-blue-200 hover:bg-blue-50"
          onClick={() => handleUnarchiveSession(selectedSession)}
        >
          <ArchiveRestore className="w-4 h-4 mr-2" />
          Unarchive
        </Button>
      )}

      {!isArchived && isClosed && (
        <Button
          size="sm"
          variant="outline"
          className="text-gray-600 border-gray-200 hover:bg-gray-50"
          onClick={() => handleArchiveSession(selectedSession)}
        >
          <Archive className="w-4 h-4 mr-2" />
          Archive
        </Button>
      )}

      {!isArchived && !isClosed && isAgentHandling && (
        <>
          <Button
            size="sm"
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            onClick={() => handleReassignToAI(selectedSession)}
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Reassign to AI
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
            onClick={openReassignModal}
          >
            <User className="w-4 h-4 mr-2" />
            Reassign to Agent
          </Button>
        </>
      )}

      {!isArchived && !isClosed && !isAgentHandling && (
        <Button
          size="sm"
          variant={isSupervising ? "default" : "outline"}
          className={isSupervising
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "text-green-600 border-green-200 hover:bg-green-50"
          }
          onClick={() => setIsSupervising(!isSupervising)}
        >
          <Eye className="w-4 h-4 mr-2" />
          {isSupervising ? "Supervising" : "Supervise Chat"}
        </Button>
      )}

      {/* Close button */}
      {!isClosed && !isArchived && (
        <Button
          size="sm"
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => handleCloseSession(selectedSession)}
        >
          <X className="w-4 h-4 mr-2" />
          Close
        </Button>
      )}
    </>
  );
};

// Helper component for reply box
const ReplyBox = ({
  currentSession,
  isSupervising,
  agentReply,
  setAgentReply,
  handleAgentReply,
  isReplying
}) => {
  const isAgentHandling = currentSession?.status === 'agent_active' || currentSession?.status === 'waiting_for_agent';
  const isClosed = currentSession?.status === 'closed';
  const isArchived = currentSession?.archived;

  // Show read-only message for closed/archived sessions
  if (isClosed || isArchived) {
    return (
      <div className="bg-gray-100 border-t border-gray-200 p-4 text-center">
        <p className="text-sm text-gray-500">
          {isArchived ? (
            <>
              <Archive className="w-4 h-4 inline mr-2" />
              This chat has been archived (read-only)
            </>
          ) : (
            <>
              <X className="w-4 h-4 inline mr-2" />
              This chat has been closed (read-only)
            </>
          )}
        </p>
      </div>
    );
  }

  const showReplyBox = isAgentHandling || isSupervising;

  if (!showReplyBox) {
    return (
      <div className="bg-gray-50 border-t border-gray-200 p-4 text-center">
        <p className="text-sm text-gray-500">
          <Eye className="w-4 h-4 inline mr-2" />
          Click "Supervise Chat" to send messages while AI is handling
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border-t border-gray-200 p-4">
      {isSupervising && !isAgentHandling && (
        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700 flex items-center gap-2">
            <Eye className="w-3 h-3" />
            Supervising Mode: Your messages will be sent as agent responses
          </p>
        </div>
      )}
      <div className="flex gap-3">
        <Textarea
          value={agentReply}
          onChange={(e) => setAgentReply(e.target.value)}
          placeholder="Type your message..."
          rows={2}
          className="flex-1 resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !isReplying) {
              e.preventDefault();
              handleAgentReply();
            }
          }}
        />
        <Button
          onClick={handleAgentReply}
          disabled={!agentReply.trim() || isReplying}
          className="h-full px-6 bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44]"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const ChatsPage = () => {
  const { agent, sessions: defaultSessions, fetchSessions, realtimeMessages } = useOutletContext();
  const [chatsSessions, setChatsSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [agentReply, setAgentReply] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [agents, setAgents] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [isSupervising, setIsSupervising] = useState(false);

  // Fetch sessions with ChatsPage filter (exclude browsing sessions with 0 messages)
  useEffect(() => {
    const fetchChatsPageSessions = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return;

        const response = await axios.get(`${API}/sessions?filter_mode=chats`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        setChatsSessions(response.data || []);
      } catch (error) {
        logger.error("Error fetching chats sessions:", error);
        // Fallback to default sessions if fetch fails
        setChatsSessions(defaultSessions);
      }
    };

    fetchChatsPageSessions();
  }, [defaultSessions]);

  // Use chatsSessions for ChatsPage (filtered to exclude browsing with 0 messages)
  const sessions = chatsSessions;

  // Handle real-time message updates from WebSocket (via AdminLayout)
  useEffect(() => {
    if (realtimeMessages && realtimeMessages.sessionId === selectedSession && realtimeMessages.messages?.length > 0) {
      setSessionMessages(prev => {
        // Filter out duplicates by message ID
        const existingIds = new Set(prev.map(m => m.id));
        const newMsgs = realtimeMessages.messages.filter(m => !existingIds.has(m.id));
        if (newMsgs.length > 0) {
          return [...prev, ...newMsgs];
        }
        return prev;
      });
    }
  }, [realtimeMessages, selectedSession]);

  const fetchSessionMessages = async (sessionId) => {
    try {
      setSelectedSession(sessionId);
      setIsSupervising(false);

      // Get access token from localStorage
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        logger.error("No access token available");
        return;
      }

      const response = await axios.get(`${API}/session/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      // Deduplicate messages by ID to prevent rendering issues
      const messages = response.data || [];
      const uniqueMessages = [];
      const seenIds = new Set();
      for (const msg of messages) {
        const msgId = msg.id || `${msg.session_id}-${msg.timestamp}-${msg.role}`;
        if (!seenIds.has(msgId)) {
          seenIds.add(msgId);
          uniqueMessages.push(msg);
        }
      }
      setSessionMessages(uniqueMessages);
    } catch (error) {
      logger.error("Error fetching messages:", error);
    }
  };

  const handleAgentReply = async () => {
    if (!agentReply.trim() || !selectedSession || isReplying) return;

    setIsReplying(true);
    try {
      await axios.post(`${API}/agent-reply`, {
        session_id: selectedSession,
        message: agentReply,
        agent_name: agent.name
      });

      setAgentReply("");
      await fetchSessionMessages(selectedSession);
      await fetchSessions();
    } catch (error) {
      logger.error("Error sending reply:", error);
      alert("Failed to send message");
    } finally {
      setIsReplying(false);
    }
  };

  const handleCloseSession = async (sessionId) => {
    if (!window.confirm("Close this chat session?")) return;

    try {
      await axios.post(`${API}/session/close`, {
        session_id: sessionId,
        agent_id: agent?.id
      });
      await fetchSessions();
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setSessionMessages([]);
      }
    } catch (error) {
      logger.error("Error closing session:", error);
    }
  };

  const handleReassignToAI = async (sessionId) => {
    if (!window.confirm("Reassign this conversation back to AI assistant?")) return;

    try {
      await axios.post(`${API}/session/reassign-to-ai`, {
        session_id: sessionId,
        agent_id: agent?.id
      });
      await fetchSessions();
      if (selectedSession === sessionId) {
        await fetchSessionMessages(sessionId);
      }
      alert("Session reassigned to AI successfully");
    } catch (error) {
      logger.error("Error reassigning session:", error);
      alert("Error reassigning session");
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents?requester_id=${agent.id}`);
      setAgents(response.data || []);
    } catch (error) {
      logger.error("Error fetching agents:", error);
    }
  };

  const handleReassignToAgent = async () => {
    if (!selectedAgentId) {
      alert("Please select an agent");
      return;
    }

    try {
      await axios.post(`${API}/session/reassign-to-agent`, {
        session_id: selectedSession,
        agent_id: selectedAgentId
      });
      await fetchSessions();
      if (selectedSession) {
        await fetchSessionMessages(selectedSession);
      }
      setShowReassignModal(false);
      setSelectedAgentId("");
      alert("Session reassigned successfully");
    } catch (error) {
      logger.error("Error reassigning session:", error);
      alert(error.response?.data?.detail || "Error reassigning session");
    }
  };

  const openReassignModal = async () => {
    setShowReassignModal(true);
    await fetchAgents();
  };

  const handleArchiveSession = async (sessionId) => {
    if (!window.confirm("Archive this chat session?")) return;

    try {
      await axios.post(`${API}/session/archive`, {
        session_id: sessionId
      });
      await fetchSessions();
    } catch (error) {
      logger.error("Error archiving session:", error);
      alert("Error archiving session");
    }
  };

  const handleUnarchiveSession = async (sessionId) => {
    if (!window.confirm("Unarchive this chat session?")) return;

    try {
      await axios.post(`${API}/session/unarchive`, {
        session_id: sessionId
      });
      await fetchSessions();
    } catch (error) {
      logger.error("Error unarchiving session:", error);
      alert("Error unarchiving session");
    }
  };

  // Helper function to format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) {
      return `Today, ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday, ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return `${dateStr}, ${timeStr}`;
    }
  };

  // Filter and sort sessions (most recent first)
  // Current chats = active, waiting_for_agent, agent_active (non-closed, non-archived)
  // Archived = closed OR explicitly archived
  const filteredSessions = sessions
    .filter(session => {
      const matchesSearch = !searchQuery ||
        session.session_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.user_data?.name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Archived filter: show closed OR archived sessions
      if (filterStatus === "archived") {
        return matchesSearch && (session.archived || session.status === "closed");
      }

      // For all other filters, hide closed and archived sessions (they belong in archives)
      if (session.archived || session.status === "closed") {
        return false;
      }

      const matchesFilter = filterStatus === "all" || session.status === filterStatus;

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  const currentSession = sessions.find(s => s.session_id === selectedSession);

  return (
    <div className="h-full flex">
      {/* Chat List */}
      <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
        {/* Search and Filter */}
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={filterStatus === "all" ? "default" : "outline"}
              onClick={() => setFilterStatus("all")}
              className="flex-1"
            >
              All
            </Button>
            <Button
              size="sm"
              variant={filterStatus === "active" ? "default" : "outline"}
              onClick={() => setFilterStatus("active")}
              className="flex-1"
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={filterStatus === "agent_active" ? "default" : "outline"}
              onClick={() => setFilterStatus("agent_active")}
              className="flex-1"
            >
              Mine
            </Button>
            <Button
              size="sm"
              variant={filterStatus === "archived" ? "default" : "outline"}
              onClick={() => setFilterStatus("archived")}
              className="flex-1 text-gray-500"
            >
              <Archive className="w-3 h-3 mr-1" />
              Archives
            </Button>
          </div>
        </div>

        {/* Session List */}
        <div className="flex-1 overflow-y-auto">
          {filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageSquare className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-sm">No conversations found</p>
            </div>
          ) : (
            filteredSessions.map((session) => {
              const isSelected = selectedSession === session.session_id;
              const isNew = session.updated_at &&
                (new Date() - new Date(session.updated_at)) < 10000;

              return (
                <div
                  key={session.session_id}
                  onClick={() => fetchSessionMessages(session.session_id)}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-all hover:bg-gray-50 ${
                    isSelected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <SessionAvatar session={session} />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">
                          {session.user_data?.name || "Customer"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(session.updated_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    {isNew && (
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <SessionStatusBadge session={session} />
                    {session.flow_type && (
                      <Badge variant="outline" className="text-xs">
                        {session.flow_type}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 bg-gray-50 flex flex-col">
        {selectedSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">
                    {currentSession?.user_data?.name || "Chat"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Session ID: {selectedSession.slice(0, 12)}...
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchSessionMessages(selectedSession)}
                  >
                    <RefreshCcw className="w-4 h-4" />
                  </Button>

                  <SessionActionButtons
                    currentSession={currentSession}
                    selectedSession={selectedSession}
                    isSupervising={isSupervising}
                    setIsSupervising={setIsSupervising}
                    handleReassignToAI={handleReassignToAI}
                    handleUnarchiveSession={handleUnarchiveSession}
                    handleArchiveSession={handleArchiveSession}
                    openReassignModal={openReassignModal}
                    handleCloseSession={handleCloseSession}
                  />
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {sessionMessages.map((msg) => (
                <div
                  key={msg.id || `${msg.session_id}-${msg.timestamp}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      msg.role === "user"
                        ? "bg-blue-600 text-white rounded-br-sm"
                        : msg.role === "agent"
                        ? "bg-green-600 text-white rounded-bl-sm"
                        : "bg-white text-gray-800 rounded-bl-sm border border-gray-200"
                    }`}
                  >
                    {/* Agent message header */}
                    {msg.role === "agent" && (
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 opacity-90" />
                        <p className="text-xs font-semibold opacity-90">
                          {msg.metadata?.agent_name || "Agent"}
                        </p>
                      </div>
                    )}
                    {/* AI Assistant header */}
                    {msg.role === "assistant" && (
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="w-4 h-4 text-blue-600" />
                        <p className="text-xs font-semibold text-gray-500">AI Assistant</p>
                      </div>
                    )}
                    {/* Use MessageFormatter for assistant and agent messages */}
                    {(msg.role === "assistant" || msg.role === "agent") ? (
                      <div className="text-sm leading-relaxed">
                        <MessageFormatter content={msg.content} />
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                    <span className="text-xs opacity-70 mt-2 block">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Box */}
            <ReplyBox
              currentSession={currentSession}
              isSupervising={isSupervising}
              agentReply={agentReply}
              setAgentReply={setAgentReply}
              handleAgentReply={handleAgentReply}
              isReplying={isReplying}
            />
          </>
        ) : (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-center p-12">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-blue-200 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                <div className="relative p-6 bg-white rounded-full shadow-lg">
                  <MessageSquare className="w-16 h-16 text-blue-500" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Select a conversation to begin
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                Choose a chat from the list on the left to view messages and continue the conversation
              </p>
              <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Real-time updates</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  <span>{sessions.length} active chats</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reassign to Agent Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Reassign to Agent</h3>
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setSelectedAgentId("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Select an agent to reassign this conversation to:
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Agents
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select an agent...</option>
                {agents
                  .filter((a) => a.id !== agent.id && a.is_active)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email}) {a.is_online ? "🟢" : "⚫"}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowReassignModal(false);
                  setSelectedAgentId("");
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReassignToAgent}
                disabled={!selectedAgentId}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reassign
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatsPage;